import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";

const bulletListItemSx = {
  display: "flex",
  alignItems: "flex-start",
  mb: 0.75,
};

const bulletTextSx = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  lineHeight: 1.5,
};

const readOnlyDisplaySx = {
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.23)",
  borderRadius: 1,
  px: 1.5,
  py: 1.25,
  minHeight: 40,
  display: "flex",
  alignItems: "flex-start",
  backgroundColor: "rgba(205, 205, 205, 0.23)",
};

function ReadOnlyDisplayField({ label, value, minHeight = 40 }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.75,
          color: "text.secondary",
          fontWeight: 500,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>

      <Box sx={{ ...readOnlyDisplaySx, minHeight }}>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: 1.5,
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function PageOneReviewSection({
  jobOrder,
  client,
  selectedJobTypes = [],
  selectedJobDescriptions = [],
  jobOrderParameters,
  errors,
  onStatusChange,
  showStatus = true,
  readOnly = false,
}) {
  return (
    <>
      {/* Basic Information Section */}
      <Grid
        item
        xs={12}
        md={12}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Card
          variant="outlined"
          sx={{
            marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
            marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
            flex: 1,
          }}
        >
          <CardHeader
            title={
              <Typography variant="h6">
                Basic Information
              </Typography>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={12} xl={2}>
                <TextField
                  name="id"
                  label="Job Order ID"
                  value={jobOrder?.id || ""}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="client_id"
                  label="Client ID"
                  value={jobOrder?.client?.id || jobOrder?.client_id || ""}
                  fullWidth
                  disabled
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <ReadOnlyDisplayField
                  label="Client Name"
                  value={jobOrder?.client?.name || ""}
                  minHeight={64}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={10}>
                    <ReadOnlyDisplayField
                      label="Job Site Address"
                      value={
                        jobOrder?.client?.address
                          ? `${jobOrder?.client?.address.house_unit_street || ""}, ${jobOrder?.client?.address.barangay || ""}, ${jobOrder?.client?.address.city || ""}, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""}`
                          : ""
                      }
                      minHeight={64}
                    />
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} xl={10}>
                    <TextField
                      name="area"
                      label="Area"
                      value={jobOrder?.client?.address?.area || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={12} lg={12} xl={4}>
                <ReadOnlyDisplayField
                  label="Contact Person"
                  value={
                    jobOrder?.client?.contact
                      ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""}`
                      : ""
                  }
                  minHeight={64}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={12} xl={4}>
                <ReadOnlyDisplayField
                  label="Contact Email"
                  value={jobOrder?.client?.contact?.email?.email || ""}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={12} xl={4}>
                <ReadOnlyDisplayField
                  label="Contact Number"
                  value={
                    jobOrder?.client?.contact?.number
                      ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={12} lg={4}>
                <TextField
                  label="Location Type"
                  value={client?.location_type || ""}
                  fullWidth
                  variant="outlined"
                  sx={{ fontSize: "0.875rem" }}
                  disabled
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Job Type Section */}
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardHeader
            title={<Typography variant="subtitle2">Job Type</Typography>}
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1, px: 2 }}>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {selectedJobTypes.map((option) => (
                <Box component="li" key={option} sx={bulletListItemSx}>
                  <CircleIcon
                    fontSize="inherit"
                    sx={{ mr: 1, mt: "0.35rem", fontSize: "0.5rem", flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={bulletTextSx}>
                    {option}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Job Description Section */}
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardHeader
            title={<Typography variant="subtitle2">Job Description</Typography>}
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1, px: 2 }}>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {selectedJobDescriptions.map((option) => (
                <Box component="li" key={option} sx={bulletListItemSx}>
                  <CircleIcon
                    fontSize="inherit"
                    sx={{ mr: 1, mt: "0.35rem", fontSize: "0.5rem", flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={bulletTextSx}>
                    {option}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Special Notes Section */}
      <Grid
        item
        xs={12}
        md={12}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Card
          variant="outlined"
          sx={{
            marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
            flex: 1,
          }}
        >
          <CardHeader
            title={
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "1rem",
                }}
              >
                Special Notes
              </Typography>
            }
          />
          <CardContent sx={{ flex: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ReadOnlyDisplayField
                  // label="Special Notes"
                  value={jobOrder?.special_notes || ""}
                  minHeight={120}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Appointment Section */}
      <Grid
        item
        xs={12}
        md={12}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Card
          variant="outlined"
          sx={{
            marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
            flex: 1,
          }}
        >
          <CardHeader
            title={
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "1rem",
                }}
              >
                Appointment
              </Typography>
            }
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ flex: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6} xl={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="appointment-type-label">
                    Appointment Type
                  </InputLabel>
                  <Select
                    labelId="appointment-type-label"
                    value={jobOrder?.appointment_type || ""}
                    label="Appointment Type"
                    disabled
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="Initial">Initial</MenuItem>
                    <MenuItem value="Follow-up">Follow-up</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} lg={6} xl={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="treatment-type-label">
                    Treatment Type
                  </InputLabel>
                  <Select
                    labelId="treatment-type-label"
                    value={jobOrder?.treatment_type || ""}
                    label="Treatment Type"
                    disabled
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="QB">QB</MenuItem>
                    <MenuItem value="FFUP">FFUP</MenuItem>
                    <MenuItem value="LT">LT</MenuItem>
                    <MenuItem value="QBLT">QBLT</MenuItem>
                    <MenuItem value="Backjob">Backjob</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Job Order Status */}
      {showStatus && (
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Status
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12} xl={2}>
                  {readOnly ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          display: "block",
                          mb: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        Job Order Status
                      </Typography>

                      <Chip
                        label={jobOrderParameters?.status || "N/A"}
                        color={
                          jobOrderParameters?.status === "Done"
                            ? "success"
                            : jobOrderParameters?.status === "Cancelled By Client"
                            ? "error"
                            : "default"
                        }
                        sx={{
                          height: 32,
                          fontWeight: 600,
                          width: "100%", // optional: makes it look like a field
                        }}
                      />
                    </Box>
                  ) : (
                    <FormControl fullWidth size="small" error={!!errors?.status}>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>

                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) => onStatusChange?.(e.target.value)}
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>

                      {errors?.status && (
                        <FormHelperText>{errors.status.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </>
  );
}

export default PageOneReviewSection;